import { useEffect, useRef, useState } from "react";

/**
 * Subscribe to /api/ws/meetings/{meetingId}/captions.
 * Returns { captions, alert } — `captions` is the rolling list of caption
 * messages and `alert` is the most recent agenda_alert (or null).
 */
export default function useLiveCaptions(meetingId, enabled = true, max = 8, authToken = null) {
  const [captions, setCaptions] = useState([]);
  const [alert, setAlert] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!enabled || !meetingId) return undefined;
    const token = authToken || localStorage.getItem("meetiq_token");
    if (!token) return undefined;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = backendUrl.replace(/^http/i, "ws");
    const url = `${wsBase}/api/ws/meetings/${meetingId}/captions?token=${encodeURIComponent(token)}`;

    let ws;
    let pingId;
    try {
      ws = new WebSocket(url);
    } catch (_e) {
      return undefined;
    }
    wsRef.current = ws;

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "caption" && msg.text) {
          // eslint-disable-next-line
          setCaptions((prev) => {
            const next = [...prev, { text: msg.text, speaker: msg.speaker, language: msg.language, ts: msg.ts || Date.now() }];
            return next.slice(-max);
          });
        } else if (msg.type === "agenda_alert") {
          // eslint-disable-next-line
          setAlert(msg);
        }
      } catch (_err) { /* ignore parse errors */ }
    };
    ws.onopen = () => {
      pingId = setInterval(() => {
        try { ws.readyState === 1 && ws.send("ping"); } catch (_e) { /* noop */ }
      }, 25000);
    };
    ws.onerror = () => { /* swallow; onclose will handle */ };
    ws.onclose = () => {
      if (pingId) clearInterval(pingId);
    };

    return () => {
      if (pingId) clearInterval(pingId);
      try { ws.close(); } catch (_e) { /* noop */ }
    };
  }, [meetingId, enabled, max, authToken]);

  return { captions, alert };
}
